import React, { MutableRefObject, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, Link, useSearchParams } from 'react-router-dom'
import useAuth from '../../../../../hooks/useAuth'
import { Toaster } from 'react-hot-toast'
import { Breadcrumb } from '../../../../../components/ui/breadcrumb'
import { formatCashAmount, isInterface } from '../../../../../lib/utils'
import axios from '../../../../../lib/axios'
import { convertToDate } from '../../../../../lib/date'

import errorAnimation from '../../../../../assets/animations/error.json'
import lottie from 'lottie-web'
import { format } from 'date-fns'

import ColliOverviewSafepoint from '../../../../../components/portal/colli/ColliKeepsafeOverview'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../../../components/ui/button'
import { ChevronLeft } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

const CountedKeepsafe = () => {
    const [searchParams] = useSearchParams();
    const colli_id = searchParams.get('colli_id');
    const { auth } = useAuth()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { barcode } = useParams()
    const location = useLocation()

    const errorContainer = useRef() as MutableRefObject<HTMLDivElement>

    const [has_state, sethas_state] = useState(true);
    const [keepsafe, setkeepsafe] = useState<CountKeepsafe | StateType>(null);
    const [colliid, setcolliid] = useState(location?.state?.colliid);

    const { data:colli } = useQuery({
        queryKey: [`colli-search-${location?.state?.barcode}`],
        queryFn: async () => {
            const { data } = await axios.post(`${process.env.REACT_APP_BASE_URL}colli/search`, {
                token: auth.data?.token,
                barcode: location?.state?.barcode,
                equal: 1,
                cn: location?.state?.cn
            })

            const firstColli = Array.isArray(data?.object?.colli) ? data.object.colli[0] : null;

            return firstColli;
        },
        enabled: !(!!colli_id)
    })

    useEffect(()=>{
        if(colli) {
            setcolliid(colli?.id);
        }
    }, [colli])

    useEffect(()=>{
        if(barcode){
            if(!location.state){
                //User did not navigate to this page via the table (state is empty, state contains the keepsafe object)
                sethas_state(false)
                return;
            }

            setkeepsafe(location.state)
        }
    }, [])

    useEffect(()=>{
        if(!has_state){
            const instance = lottie.loadAnimation({
                container: errorContainer.current,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                animationData: errorAnimation
            })
            return () => instance.destroy()
        }
    }, [has_state])

  return (
    <>
        <Toaster />
        <div className="relative w-full p-4 overflow-auto md:p-8">
            <div className='flex items-center justify-between'>
                <div className='flex flex-col'>
                    <Breadcrumb items={[{title:<p onClick={()=>{navigate('/track-en-trace')}} className='cursor-pointer'>{t("track_and_trace.o_detail.link_tt")}</p>}, {title:`${t("track_and_trace.o_detail.link_keepsafe")} ${barcode}`}]}/>
                    <h1 className='text-2xl font-semibold'>{t("track_and_trace.o_detail.title_counted_keepsafe")}</h1>
                </div>

                <Link to="/track-en-trace"><Button variant='ghost' className='gap-2'><ChevronLeft className='w-4 h-4'/> {t("track_and_trace.o_detail.back")}</Button></Link>
            </div>

            {has_state &&
            <>
            <div className='flex flex-col gap-4 p-4 mt-4 border rounded-sm md:mt-8'>
                <h2 className='text-lg'>{t("track_and_trace.o_detail.title_detail_keepsafe")}</h2>
                {isInterface(keepsafe, {} as CountKeepsafe) &&
                <div className='grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-2'>
                    <div className='flex gap-2'>
                        <p className='font-semibold'>ID</p>
                        <p className='text-neutral-500'>{location?.state?.colliid?.toString() !== '0' ? location?.state?.colliid : colliid}</p>
                    </div>
                    <div className='flex gap-2'>
                        <p className='font-semibold'>{t("track_and_trace.customer")}</p>
                        <p className='text-neutral-500'>{keepsafe?.name}</p>
                    </div>
                    <div className='flex gap-2'>
                        <p className='font-semibold'>{t("track_and_trace.barcode")}</p>
                        <p className='text-neutral-500'>{keepsafe?.barcode}</p>
                    </div>
                    <div className='flex gap-2'>
                        <p className='font-semibold'>{t("track_and_trace.motherks")}</p>
                        <p className='text-neutral-500'>{keepsafe?.motherks}</p>
                    </div>
                    <div className='flex gap-2'>
                        <p className='font-semibold'>{t("track_and_trace.dtstock")}</p>
                        <p className='text-neutral-500'>{format(convertToDate(keepsafe.dtstock) || new Date(), "dd/MM/yyyy")}</p>
                    </div>
                    <div className='flex gap-2'>
                        <p className='font-semibold'>{t("track_and_trace.borderel")}</p>
                        <p className='text-neutral-500'>{keepsafe?.borderel}</p>
                    </div>
                    <div className='flex gap-2'>
                        <p className='font-semibold'>{t("track_and_trace.bgannounced")}</p>
                        <p className='text-neutral-500'>{keepsafe?.bgannounced ? formatCashAmount(keepsafe?.bgannounced) : '0'}</p>
                    </div>
                    <div className='flex gap-2'>
                        <p className='font-semibold'>{t("track_and_trace.bgcount")}</p>
                        <p className='text-neutral-500'>{keepsafe?.bgcount ? formatCashAmount(keepsafe?.bgcount) : '0'}</p>
                    </div>
                    <div className='flex gap-2'>
                        <p className='font-semibold'>{t("track_and_trace.bgdamaged")}</p>
                        <p className='text-neutral-500'>{keepsafe?.bgdamaged ? formatCashAmount(keepsafe?.bgdamaged) : '0'}</p>
                    </div>
                    <div className='flex gap-2'>
                        <p className='font-semibold'>{t("track_and_trace.bgfalse")}</p>
                        <p className='text-neutral-500'>{keepsafe?.bgfalse ? formatCashAmount(keepsafe?.bgfalse) : '0'}</p>
                    </div>
                    <div className='flex gap-2'>
                        <p className='font-semibold'>{t("track_and_trace.bginkstained")}</p>
                        <p className='text-neutral-500'>{keepsafe?.bginkstained ? formatCashAmount(keepsafe?.bginkstained) : '0'}</p>
                    </div>
                </div>
                }
            </div>

            <div className='flex flex-col gap-4 py-4 mt-4 border rounded-sm md:mt-8'>
                {colli_id ? <ColliOverviewSafepoint add_days={0} colliid={colli_id ? parseInt(colli_id) : colliid} keepsafeDate={isInterface(keepsafe, {} as CountKeepsafe) ? keepsafe.dtcreation : location.state.dtcreation} barcode={location.state?.barcode}/> : <></>}
            </div>
            </> 
            }

            {!has_state && 
            <div className={`flex flex-col items-center justify-center`}>
                <div className='w-[240px] h-[240px]' ref={errorContainer}></div>
                <h2 className='mb-4 text-2xl font-semibold'>{t("track_and_trace.o_detail.error_title")}</h2>
                <p className='text-center'>{t("track_and_trace.o_detail.error_description_p8")}<br></br>{t("track_and_trace.o_detail.error_description_p2")} <Link to='/track-en-trace' className='font-medium underline'>{t("track_and_trace.o_detail.error_description_p3")}</Link> {t("track_and_trace.o_detail.error_description_p4")}</p>
            </div>
            }
        </div>
    </>
  )
}

export default CountedKeepsafe

export type SafepointSubpage = 'announcement' | 'count' | 'transport' | 'history' | 'scans'